import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Lock, Mail, Phone, User, Truck, Wallet, ScanEye, RotateCcw, ArrowLeft, Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import { AUTH } from '@/constants/testIds';
import { DELIVERY_FEE } from '@/constants/shop';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import BrandLogo from '@/components/BrandLogo';

// حقل بأيقونة + رسالة خطأ تحته
const Field = ({ id, label, icon: Icon, error, children, hint }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-cairo font-semibold text-gray-700 mb-1.5">{label}</label>
    <div
      className={cn(
        'group relative flex items-center rounded-2xl border bg-white transition-all duration-200',
        'focus-within:border-burgundy-500 focus-within:ring-4 focus-within:ring-burgundy-500/10',
        error ? 'border-rose-400 ring-4 ring-rose-500/10' : 'border-gray-200 hover:border-gray-300'
      )}
    >
      <Icon className={cn('absolute right-4 w-5 h-5 transition-colors', error ? 'text-rose-400' : 'text-gray-400 group-focus-within:text-burgundy-500')} />
      {children}
    </div>
    {error ? <p className="mt-1.5 text-xs font-cairo text-rose-600">{error}</p> : hint}
  </div>
);

const inputCls = 'w-full h-12 bg-transparent pr-12 pl-4 font-cairo text-[15px] text-brand-black placeholder:text-gray-400 focus:outline-none rounded-2xl';

// قوة كلمة المرور (0-4): الطول، حروف + أرقام، رموز، طول كبير
const strengthOf = (pw) => {
  if (!pw) return 0;
  const hasLetter = /[A-Za-z؀-ۿ]/.test(pw);
  const hasDigit = /\d/.test(pw);
  const hasSymbol = /[^A-Za-z0-9؀-ۿ\s]/.test(pw);
  return Math.max(1, [pw.length >= 8, hasLetter && hasDigit, hasSymbol, pw.length >= 12].filter(Boolean).length);
};
const STRENGTH = [
  { label: '', cls: 'bg-gray-200' },
  { label: 'ضعيفة', cls: 'bg-rose-500' },
  { label: 'مقبولة', cls: 'bg-amber-500' },
  { label: 'جيدة', cls: 'bg-emerald-500' },
  { label: 'قوية جداً', cls: 'bg-emerald-600' },
];

// أخطاء التحقق من السيرفر (422) بالعربي، لكل حقل
const translate = (field, msg = '') => {
  if (/taken|مسجل|مستخدم/i.test(msg)) return 'الإيميل ده مسجّل قبل كده — جرّبي تسجيل الدخول.';
  if (/valid email|صالح/i.test(msg)) return 'اكتبي إيميل صحيح.';
  if (/at least|min/i.test(msg) && field === 'password') return 'كلمة المرور لازم تكون 6 حروف على الأقل.';
  if (/required|مطلوب/i.test(msg)) return 'الحقل ده مطلوب.';
  return msg;
};

const Login = () => {
  const location = useLocation();
  const [mode, setMode] = useState(location.pathname === '/register' || location.state?.register ? 'register' : 'login');
  const isLogin = mode === 'login';
  const [form, setForm] = useState({ email: '', password: '', fullName: '', phone: '' });
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const { login, register, user } = useAuth();
  const site = useSettings();
  const navigate = useNavigate();
  const from = location.state?.from || '/';

  useEffect(() => {
    if (user) navigate(from, { replace: true });
  }, [user]);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    if (errors[key] || errors.general) setErrors((er) => ({ ...er, [key]: undefined, general: undefined }));
  };

  const switchMode = (next) => {
    setMode(next);
    setErrors({});
    setShowPw(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});
    try {
      if (isLogin) {
        await login(form.email, form.password);
        toast.success('أهلاً بعودتك ✨');
      } else {
        await register(form.email, form.fullName, form.phone, form.password);
        toast.success('تم إنشاء حسابك بنجاح 🎉');
      }
      navigate(from, { replace: true });
    } catch (error) {
      const data = error.response?.data;
      const fieldErrors = data?.errors || {};
      const mapped = {
        email: fieldErrors.email && translate('email', fieldErrors.email[0]),
        password: fieldErrors.password && translate('password', fieldErrors.password[0]),
        fullName: fieldErrors.name && translate('name', fieldErrors.name[0]),
        phone: fieldErrors.phone && translate('phone', fieldErrors.phone[0]),
      };
      const hasField = Object.values(mapped).some(Boolean);
      setErrors({
        ...mapped,
        general: hasField ? undefined : data?.message || (isLogin ? 'تعذّر تسجيل الدخول، حاولي مرة أخرى.' : 'تعذّر إنشاء الحساب، حاولي مرة أخرى.'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const strength = strengthOf(form.password);

  const perks = [
    { icon: Truck, title: 'توصيل لكل المملكة', text: `رسوم ثابتة ${DELIVERY_FEE} د.أ` },
    { icon: Wallet, title: 'الدفع عند الاستلام', text: 'من غير بطاقات' },
    { icon: ScanEye, title: 'معاينة قبل الدفع', text: 'شوفي القطعة بإيدك' },
    { icon: RotateCcw, title: 'استبدال واسترجاع', text: 'حسب سياسة الاسترجاع' },
  ];

  return (
    <div className="min-h-[calc(100vh-5rem)] -mb-24 bg-[#f7f2ee] dark:bg-transparent pt-6 md:pt-12 pb-32 md:pb-36 px-4">
      <div className="mx-auto max-w-6xl grid lg:grid-cols-[1.05fr_1fr] rounded-[2rem] overflow-hidden bg-white dark:bg-gray-900 shadow-[0_40px_100px_-40px_rgba(114,47,55,0.45)] ring-1 ring-black/5 auth-rise">
        {/* ===== النص البصري ===== */}
        <aside className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-[#5C0B11] text-white p-12">
          <div className="absolute inset-0 bg-gradient-to-br from-[#8a3a44] via-[#722F37] to-[#3d0a0f]" />
          <div className="absolute inset-0 opacity-60" style={{ backgroundImage: 'radial-gradient(rgba(244,213,141,0.22) 1px, transparent 1.5px)', backgroundSize: '22px 22px' }} />
          <div className="absolute -top-28 -left-28 w-96 h-96 rounded-full border-[48px] border-[#F4D58D]/10 auth-spin-slow" />
          <div className="absolute -bottom-40 -right-24 w-[28rem] h-[28rem] rounded-full bg-[#F4D58D]/10 blur-3xl" />
          <div className="absolute top-1/3 left-10 w-3 h-3 rounded-full bg-[#F4D58D] auth-float" />
          <div className="absolute bottom-40 left-1/3 w-2 h-2 rounded-full bg-[#F4D58D]/70 auth-float [animation-delay:1.2s]" />

          <div className="relative">
            <Link to="/" className="inline-block">
              {site.logo_url ? (
                <BrandLogo imgClassName="h-16 w-auto bg-white rounded-xl p-2" />
              ) : (
                <span className="font-tajawal font-extrabold text-5xl text-[#F4D58D]">{site.site_name}</span>
              )}
            </Link>
          </div>

          <div className="relative">
            <p className="inline-flex items-center gap-2 text-sm font-cairo font-bold text-[#F4D58D] mb-5">
              <Sparkles className="w-4 h-4" /> أناقتكِ تبدأ من هنا
            </p>
            <h2 className="font-tajawal font-extrabold text-5xl leading-[1.25] mb-5">
              {isLogin ? (
                <>أهلاً بعودتكِ<br /><span className="text-[#F4D58D]">اشتقنالك</span></>
              ) : (
                <>انضمي لعائلة<br /><span className="text-[#F4D58D]">{site.site_name}</span></>
              )}
            </h2>
            <p className="font-cairo text-lg text-white/80 max-w-md leading-relaxed">
              {isLogin
                ? 'سجّلي دخولك وكمّلي من حيث وقفتِ — سلتك ومفضلتك وطلباتك محفوظين.'
                : 'حساب واحد يحفظ عناوينك وطلباتك ومفضلتك، ويخلّي الطلب الجاي أسرع بكتير.'}
            </p>
          </div>

          <ul className="relative grid grid-cols-2 gap-3">
            {perks.map(({ icon: Icon, title, text }) => (
              <li key={title} className="flex items-center gap-3 rounded-2xl bg-white/[0.07] ring-1 ring-white/10 backdrop-blur-sm p-3.5">
                <span className="w-10 h-10 rounded-xl bg-[#F4D58D] text-[#5C0B11] flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5" />
                </span>
                <span className="min-w-0">
                  <span className="block font-tajawal font-bold text-sm">{title}</span>
                  <span className="block font-cairo text-xs text-white/65">{text}</span>
                </span>
              </li>
            ))}
          </ul>
        </aside>

        {/* ===== الفورم ===== */}
        <section className="relative p-6 sm:p-10 lg:p-12 flex flex-col justify-center">
          {/* الموبايل: هيدر صغير بالماركة */}
          <div className="lg:hidden -mx-6 -mt-6 sm:-mx-10 sm:-mt-10 mb-8 px-6 py-8 text-center bg-gradient-to-br from-[#8a3a44] via-[#722F37] to-[#3d0a0f] text-white relative overflow-hidden">
            <div className="absolute inset-0 opacity-50" style={{ backgroundImage: 'radial-gradient(rgba(244,213,141,0.22) 1px, transparent 1.5px)', backgroundSize: '20px 20px' }} />
            <Link to="/" className="relative inline-block">
              {site.logo_url ? <BrandLogo imgClassName="h-14 w-auto bg-white rounded-xl p-1.5" /> : <span className="font-tajawal font-extrabold text-4xl text-[#F4D58D]">{site.site_name}</span>}
            </Link>
            <p className="relative mt-2 font-cairo text-sm text-white/80">{isLogin ? 'أهلاً بعودتكِ، اشتقنالك' : 'انضمي لعائلتنا في دقيقة'}</p>
          </div>

          <div className="w-full max-w-md mx-auto">
            {/* التبديل بين الدخول والتسجيل */}
            <div className="relative grid grid-cols-2 p-1.5 rounded-2xl bg-gray-100 dark:bg-white/5 mb-8" role="tablist">
              <span
                className={cn(
                  'absolute top-1.5 bottom-1.5 w-[calc(50%-0.375rem)] rounded-xl bg-white dark:bg-gray-800 shadow-md transition-all duration-300 ease-out',
                  isLogin ? 'right-1.5' : 'right-[calc(50%+0rem)]'
                )}
              />
              {[['login', 'تسجيل الدخول'], ['register', 'حساب جديد']].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={mode === key}
                  data-testid={key === 'register' && isLogin ? AUTH.switchModeBtn : key === 'login' && !isLogin ? AUTH.switchModeBtn : undefined}
                  onClick={() => switchMode(key)}
                  className={cn('relative z-10 h-11 rounded-xl font-cairo font-bold text-sm transition-colors', mode === key ? 'text-burgundy-600' : 'text-gray-500 hover:text-gray-700')}
                >
                  {label}
                </button>
              ))}
            </div>

            <div key={mode} className="auth-fade">
              <h1 className="font-tajawal font-extrabold text-3xl text-brand-black mb-1.5">
                {isLogin ? 'مرحباً من جديد ' : 'إنشاء حساب جديد'}
              </h1>
              <p className="font-cairo text-gray-500 mb-7">
                {isLogin ? 'ادخلي بإيميلك وكلمة المرور.' : 'املي بياناتك وابدئي التسوق فوراً.'}
              </p>

              {errors.general && (
                <div role="alert" className="mb-5 flex items-start gap-2 rounded-2xl bg-rose-50 border border-rose-200 px-4 py-3 font-cairo text-sm text-rose-700">
                  <span className="mt-0.5">⚠</span> {errors.general}
                </div>
              )}

              <form data-testid={isLogin ? AUTH.loginForm : AUTH.registerForm} onSubmit={handleSubmit} className="space-y-4" noValidate={false}>
                {!isLogin && (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field id="fullName" label="الاسم الكامل" icon={User} error={errors.fullName}>
                      <input id="fullName" data-testid={AUTH.nameInput} value={form.fullName} onChange={set('fullName')} required autoComplete="name" placeholder="اسمك" className={inputCls} />
                    </Field>
                    <Field id="phone" label="رقم الهاتف" icon={Phone} error={errors.phone}>
                      <input id="phone" data-testid={AUTH.phoneInput} type="tel" inputMode="tel" dir="ltr" value={form.phone} onChange={set('phone')} required autoComplete="tel" placeholder="07XXXXXXXX" className={cn(inputCls, 'text-right')} />
                    </Field>
                  </div>
                )}

                <Field id="email" label="البريد الإلكتروني" icon={Mail} error={errors.email}>
                  <input id="email" data-testid={AUTH.emailInput} type="email" dir="ltr" value={form.email} onChange={set('email')} required autoComplete="email" placeholder="name@example.com" className={cn(inputCls, 'text-right')} />
                </Field>

                <Field
                  id="password"
                  label="كلمة المرور"
                  icon={Lock}
                  error={errors.password}
                  hint={
                    !isLogin && form.password && (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 grid grid-cols-4 gap-1">
                          {[1, 2, 3, 4].map((i) => (
                            <span key={i} className={cn('h-1.5 rounded-full transition-colors duration-300', i <= strength ? STRENGTH[strength].cls : 'bg-gray-200')} />
                          ))}
                        </div>
                        <span className="text-xs font-cairo text-gray-500 w-16 text-left">{STRENGTH[strength].label}</span>
                      </div>
                    )
                  }
                >
                  <input
                    id="password"
                    data-testid={AUTH.passwordInput}
                    type={showPw ? 'text' : 'password'}
                    value={form.password}
                    onChange={set('password')}
                    required
                    minLength={isLogin ? undefined : 6}
                    autoComplete={isLogin ? 'current-password' : 'new-password'}
                    placeholder={isLogin ? '••••••••' : '6 حروف على الأقل'}
                    className={cn(inputCls, 'pl-12')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    aria-label={showPw ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
                    className="absolute left-3 w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-burgundy-500 hover:bg-gray-50 transition"
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </Field>

                <button
                  data-testid={AUTH.submitBtn}
                  type="submit"
                  disabled={submitting}
                  className="group relative w-full py-3.5 mt-2 rounded-2xl overflow-hidden bg-gradient-to-l from-burgundy-500 to-[#8a3a44] text-white font-cairo font-bold text-base shadow-lg shadow-burgundy-500/25 transition-all hover:shadow-xl hover:shadow-burgundy-500/30 hover:-translate-y-0.5 disabled:opacity-70 disabled:hover:translate-y-0"
                >
                  <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-l from-transparent via-white/20 to-transparent" />
                  <span className="relative inline-flex items-center justify-center gap-2">
                    {submitting ? (
                      <><Loader2 className="w-5 h-5 animate-spin" /> {isLogin ? 'جاري الدخول...' : 'جاري إنشاء الحساب...'}</>
                    ) : (
                      <>{isLogin ? 'تسجيل الدخول' : 'إنشاء الحساب'} <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" /></>
                    )}
                  </span>
                </button>
              </form>

              <p className="mt-7 text-center font-cairo text-sm text-gray-500">
                {isLogin ? 'أول مرة معانا؟' : 'عندك حساب بالفعل؟'}{' '}
                <button type="button" onClick={() => switchMode(isLogin ? 'register' : 'login')} className="font-bold text-burgundy-500 hover:underline">
                  {isLogin ? 'أنشئي حساب مجاناً' : 'سجّلي دخولك'}
                </button>
              </p>

              {!isLogin && (
                <p className="mt-4 text-center font-cairo text-xs text-gray-400 leading-relaxed">
                  بإنشاء الحساب أنتِ موافقة على{' '}
                  <Link to="/terms" className="underline hover:text-burgundy-500">الشروط</Link> و{' '}
                  <Link to="/privacy" className="underline hover:text-burgundy-500">سياسة الخصوصية</Link>
                </p>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Login;
